import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Edit2, Trash2, MessageCircle, BarChart3, Settings, Pause, Play, CheckCircle2, Circle, ArrowRight, Lock, Crown, Eye, LifeBuoy } from 'lucide-react';
import api from '../api';
import usePiano, { avviaUpgrade } from '../hooks/usePiano';
import { TIPI, tipoLabel } from '../utils/tipi';
import { formatPrezzo } from '../utils/prezzo';
import ConfirmModal from '../components/ConfirmModal';
import SupportModal from '../components/SupportModal';
import PlanModal from '../components/PlanModal';
import './ProviderDashboard.css';

function ProviderDashboard() {
  const [stats, setStats] = useState({
    totalServizi: 0,
    totalMessaggi: 0,
    messaggiNonLetti: 0,
  });
  const [servizi, setServizi] = useState([]);
  const [messaggi, setMessaggi] = useState([]);
  const [profilo, setProfilo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [messaggiBloccati, setMessaggiBloccati] = useState(false);
  const [statistiche, setStatistiche] = useState(null);
  const [upgradeError, setUpgradeError] = useState(null);
  const [modal, setModal] = useState(null); // popup in stile sito (conferme/avvisi)
  const [supportOpen, setSupportOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const { isPro, stripeAttivo, piano_scadenza, cadenze = [] } = usePiano();
  const [searchParams] = useSearchParams();
  const upgradeEsito = searchParams.get('upgrade'); // 'success' | 'cancel' | null
  const apriPiano = searchParams.get('plan') === 'open'; // arrivo da /piani: apri la scelta piano

  // Se si arriva dalla pagina pubblica /piani (?plan=open) e non si è già Pro,
  // apri direttamente la modale di scelta del piano.
  useEffect(() => {
    if (apriPiano && !isPro) setPlanOpen(true);
  }, [apriPiano, isPro]);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        // Load services
        const serviziRes = await api.get('/servizi/provider/miei');
        const serviziData = serviziRes.data.servizi || serviziRes.data || [];
        setServizi(serviziData);

        // Load profile (per la checklist di onboarding)
        try {
          const profiloRes = await api.get('/auth/profilo');
          setProfilo(profiloRes.data);
        } catch (e) {
          // Profilo non disponibile: la checklist lo trattera' come incompleto.
        }

        // Statistiche (viste + messaggi) — best-effort, non blocca la dashboard.
        try {
          const statsRes = await api.get('/servizi/provider/statistiche');
          setStatistiche(statsRes.data);
        } catch (e) {
          // statistiche non disponibili: la dashboard funziona comunque.
        }

        // Load recent messages
        const messaggiRes = await api.get('/messaggi');
        const messaggiData = messaggiRes.data.messaggi || messaggiRes.data || [];
        setMessaggi(messaggiData.slice(0, 5));
        setMessaggiBloccati(!!messaggiRes.data.bloccato);

        // Calculate stats from loaded data
        const nonLetti = messaggiData.filter(m => !m.letto).length;
        setStats({
          totalServizi: serviziData.length,
          totalMessaggi: messaggiData.length,
          messaggiNonLetti: nonLetti,
        });
      } catch (err) {
        setError('Errore nel caricamento del dashboard');
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, []);

  const handleUpgrade = async (cadenza = 'annuale') => {
    setUpgradeError(null);
    try {
      await avviaUpgrade(cadenza);
    } catch (e) {
      setUpgradeError(e.response?.data?.errore || e.message);
    }
  };

  const handleToggleStato = async (servizio) => {
    try {
      const res = await api.patch(`/servizi/${servizio.id}/stato`, { attivo: !servizio.attivo });
      setServizi((prev) => prev.map((s) => (s.id === servizio.id ? { ...s, attivo: res.data.attivo } : s)));
    } catch (err) {
      // Limite del piano gratuito raggiunto: blocca e proponi l'upgrade.
      if (err.response?.status === 402) {
        setModal({
          title: 'Limite del piano gratuito',
          message: err.response.data?.errore || 'Hai raggiunto il limite del piano gratuito.',
          icon: <Crown size={26} />,
          confirmLabel: 'Passa a Pro',
          cancelLabel: 'Non ora',
          onConfirm: () => { setModal(null); avviaUpgrade().catch(() => {}); },
        });
        return;
      }
      setModal({
        title: 'Errore',
        message: err.response?.data?.errore || 'Errore nel cambiare lo stato della scheda',
        confirmLabel: 'Ho capito',
        cancelLabel: null,
        onConfirm: () => setModal(null),
      });
    }
  };

  const eseguiEliminazione = async (id) => {
    setModal(null);
    setDeleting(id);
    try {
      await api.delete(`/servizi/${id}`);
      setServizi(servizi.filter((s) => s.id !== id));
      setStats((prev) => ({
        ...prev,
        totalServizi: prev.totalServizi - 1,
      }));
    } catch (err) {
      console.error('Error deleting service:', err);
      setModal({
        title: 'Errore',
        message: 'Errore nell\'eliminazione della scheda. Riprova.',
        confirmLabel: 'Ho capito',
        cancelLabel: null,
        onConfirm: () => setModal(null),
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteServizio = (servizio) => {
    setModal({
      title: 'Eliminare la scheda?',
      message: `"${servizio.nome}" verrà eliminata definitivamente. L'azione non è reversibile.`,
      variant: 'danger',
      icon: <Trash2 size={24} />,
      confirmLabel: 'Elimina',
      cancelLabel: 'Annulla',
      onConfirm: () => eseguiEliminazione(servizio.id),
    });
  };

  if (loading) {
    return (
      <div className="dashboard-page loading">
        <div className="spinner" />
        Caricamento dashboard...
      </div>
    );
  }

  // Una scheda attiva per tipo: i tipi senza scheda attiva si possono ancora aggiungere.
  const tipiAttivi = new Set(servizi.filter((s) => s.attivo).map((s) => s.tipo));
  const tipiMancanti = TIPI.filter((t) => !tipiAttivi.has(t.value));

  // Gating freemium: i fornitori free possono avere 1 sola scheda. Pro = nessun limite.
  const canAddScheda = isPro || servizi.length === 0;
  const scadenzaFmt = piano_scadenza
    ? new Date(piano_scadenza).toLocaleDateString('it-IT')
    : null;

  // Checklist di onboarding: guida il nuovo fornitore a completare profilo, scheda e foto.
  const profiloCompleto = !!(
    profilo &&
    profilo.citta &&
    (profilo.telefono || profilo.whatsapp || profilo.email_contatto)
  );
  const haScheda = servizi.length > 0;
  const haFoto = servizi.some((s) => s.prima_foto);
  const schedaSenzaFoto = servizi.find((s) => !s.prima_foto);

  const onboardingSteps = [
    {
      fatto: profiloCompleto,
      titolo: 'Completa il profilo',
      desc: 'Località e contatti: è ciò che i genitori vedono per raggiungerti.',
      to: '/dashboard/profilo',
      cta: 'Vai al profilo',
    },
    {
      fatto: haScheda,
      titolo: 'Crea la tua prima scheda',
      desc: 'La scheda è l’annuncio che appare nella ricerca, con descrizione e prezzi.',
      to: '/dashboard/nuovo-servizio',
      cta: 'Crea scheda',
      disabled: false,
    },
    {
      fatto: haFoto,
      titolo: 'Aggiungi le foto',
      desc: 'Le schede con foto ricevono molti più contatti.',
      to: schedaSenzaFoto ? `/dashboard/servizio/${schedaSenzaFoto.id}/modifica` : '/dashboard/nuovo-servizio',
      cta: 'Aggiungi foto',
      disabled: !haScheda,
    },
  ];
  const onboardingCompleto = onboardingSteps.every((s) => s.fatto);
  // Il primo step non ancora completato è quello "attivo" (CTA evidenziata).
  const stepAttivo = onboardingSteps.findIndex((s) => !s.fatto && !s.disabled);

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>Dashboard Provider</h1>
            <span className={`piano-badge ${isPro ? 'pro' : 'free'}`}>
              {isPro ? <><Crown size={14} /> Pro</> : 'Free'}
            </span>
          </div>
          <div className="dashboard-header-actions">
            <Link to="/dashboard/profilo" className="btn btn-secondary">
              <Settings size={20} />
              Il mio profilo
            </Link>
            <button className="btn btn-secondary" onClick={() => setSupportOpen(true)}>
              <LifeBuoy size={20} />
              Contatta l'assistenza
            </button>
            {canAddScheda ? (
              tipiMancanti.map((t) => (
                <Link
                  key={t.value}
                  to={`/dashboard/nuovo-servizio?tipo=${t.value}`}
                  className="btn btn-primary"
                >
                  <Plus size={20} />
                  Aggiungi {t.label}
                </Link>
              ))
            ) : (
              <button className="btn btn-primary" onClick={() => setPlanOpen(true)}>
                <Crown size={18} />
                Passa a Pro per altre schede
              </button>
            )}
          </div>
        </div>

        {/* Esito ritorno da Stripe Checkout */}
        {upgradeEsito === 'success' && (
          <div className="upgrade-banner success">
            🎉 Pagamento ricevuto! Il piano Pro è ora attivo. Se non vedi subito lo sblocco, ricarica la pagina tra poco.
          </div>
        )}
        {upgradeEsito === 'cancel' && (
          <div className="upgrade-banner cancel">
            Pagamento annullato. Puoi passare a Pro quando vuoi.
          </div>
        )}
        {upgradeError && (
          <div className="error-message"><p>{upgradeError}</p></div>
        )}

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Promo upgrade per i fornitori free */}
        {!isPro && (
          <section className="pro-card">
            <div className="pro-card-text">
              <h2><Crown size={20} /> Passa a Pro</h2>
              {statistiche && (statistiche.viste_7gg > 0 || statistiche.messaggi_non_letti > 0) ? (
                <p>
                  Le tue schede sono state viste <strong>{statistiche.viste_7gg} volte</strong> negli ultimi 7 giorni
                  {statistiche.messaggi_non_letti > 0 && (
                    <> e hai <strong>{statistiche.messaggi_non_letti} richieste</strong> che non puoi ancora leggere</>
                  )}.
                  Sbloccale e rispondi ai genitori con Pro. <strong>€150/anno.</strong>
                </p>
              ) : (
                <p>
                  Sblocca più schede e la lettura/risposta dei messaggi dei genitori.
                  <strong> Da €15/mese o €150/anno.</strong>
                </p>
              )}
            </div>
            {stripeAttivo ? (
              <div className="pro-card-actions">
                {cadenze.includes('mensile') && (
                  <button className="btn btn-secondary" onClick={() => handleUpgrade('mensile')}>
                    €15/mese
                  </button>
                )}
                {cadenze.includes('annuale') && (
                  <button className="btn btn-primary" onClick={() => handleUpgrade('annuale')}>
                    €150/anno <span className="pro-risparmio">2 mesi gratis</span>
                  </button>
                )}
              </div>
            ) : (
              <button className="btn btn-primary" disabled>Pagamenti non disponibili</button>
            )}
          </section>
        )}
        {isPro && scadenzaFmt && (
          <p className="pro-scadenza"><Crown size={14} /> Piano Pro attivo fino al {scadenzaFmt}.</p>
        )}

        {/* Checklist di onboarding: visibile finché i passi non sono completati */}
        {!onboardingCompleto && (
          <section className="onboarding-card">
            <div className="onboarding-head">
              <h2>Completa il tuo account</h2>
              <p>Tre passi per essere visibile ai genitori che cercano servizi per le feste.</p>
            </div>
            <ol className="onboarding-steps">
              {onboardingSteps.map((step, idx) => (
                <li
                  key={step.titolo}
                  className={`onboarding-step${step.fatto ? ' done' : ''}${idx === stepAttivo ? ' active' : ''}`}
                >
                  <span className="onboarding-step-icon">
                    {step.fatto ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                  </span>
                  <div className="onboarding-step-body">
                    <strong>{step.titolo}</strong>
                    <span>{step.desc}</span>
                  </div>
                  {!step.fatto && (
                    step.disabled ? (
                      <span className="onboarding-step-cta disabled">{step.cta}</span>
                    ) : (
                      <Link
                        to={step.to}
                        className={`onboarding-step-cta${idx === stepAttivo ? ' primary' : ''}`}
                      >
                        {step.cta} <ArrowRight size={16} />
                      </Link>
                    )
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Eye size={28} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{statistiche ? statistiche.viste_7gg : '—'}</div>
              <div className="stat-label">Visualizzazioni (7 giorni)</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <BarChart3 size={28} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.totalServizi}</div>
              <div className="stat-label">Servizi Pubblicati</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <MessageCircle size={28} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.totalMessaggi}</div>
              <div className="stat-label">Messaggi Totali</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <MessageCircle size={28} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.messaggiNonLetti}</div>
              <div className="stat-label">Messaggi Non Letti</div>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Services Section */}
          <section className="dashboard-section services-section">
            <div className="section-header">
              <h2>I Miei Servizi</h2>
              <span className="service-count">{servizi.length}</span>
            </div>

            {servizi.length > 0 ? (
              <div className="services-list">
                {servizi.map((servizio) => (
                  <div key={servizio.id} className="service-item">
                    <div className="service-image">
                      <img
                        src={servizio.prima_foto || 'https://via.placeholder.com/100'}
                        alt={servizio.nome}
                      />
                      <span className={`service-type ${servizio.tipo}`}>
                        {tipoLabel(servizio.tipo)}
                      </span>
                    </div>
                    <div className="service-details">
                      <h3>{servizio.nome}</h3>
                      <div className="service-badges">
                        {servizio.stato_approvazione === 'in_attesa' && (
                          <span className="service-badge in-attesa">In attesa di approvazione</span>
                        )}
                        {servizio.stato_approvazione === 'rifiutata' && (
                          <span className="service-badge rifiutata" title={servizio.motivo_rifiuto || ''}>
                            Rifiutata{servizio.motivo_rifiuto ? `: ${servizio.motivo_rifiuto}` : ''}
                          </span>
                        )}
                        {servizio.stato_approvazione === 'approvata' && servizio.attivo && (
                          <span className="service-badge approvata">Pubblicata</span>
                        )}
                        {!servizio.attivo && <span className="service-badge inactive">In pausa</span>}
                      </div>
                      <p className="service-price">
                        {formatPrezzo(servizio)}
                      </p>
                    </div>
                    <div className="service-actions">
                      <button
                        className="btn-action"
                        onClick={() => handleToggleStato(servizio)}
                        title={servizio.attivo ? 'Metti in pausa' : 'Riattiva'}
                      >
                        {servizio.attivo ? <Pause size={18} /> : <Play size={18} />}
                      </button>
                      <Link
                        to={`/dashboard/servizio/${servizio.id}/modifica`}
                        className="btn-action edit"
                        title="Modifica"
                      >
                        <Edit2 size={18} />
                      </Link>
                      <button
                        className="btn-action delete"
                        onClick={() => handleDeleteServizio(servizio)}
                        disabled={deleting === servizio.id}
                        title="Elimina"
                      >
                        {deleting === servizio.id ? (
                          <span className="spinner-small" />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>Non hai ancora pubblicato servizi.</p>
                <Link to="/dashboard/nuovo-servizio" className="btn btn-primary">
                  <Plus size={18} />
                  Pubblica il tuo primo servizio
                </Link>
              </div>
            )}

            {/* Free: limite di 1 scheda → invito a Pro per crearne altre */}
            {!canAddScheda && servizi.length > 0 && (
              <div className="services-upgrade-hint">
                <Lock size={18} />
                <p>Con il piano gratuito puoi avere <strong>una sola scheda</strong>. Passa a Pro per crearne altre.</p>
                <button className="btn btn-primary" onClick={() => setPlanOpen(true)}>
                  <Crown size={18} /> Passa a Pro
                </button>
              </div>
            )}
          </section>

          {/* Recent Messages Section */}
          <section className="dashboard-section messages-section">
            <div className="section-header">
              <h2>Messaggi Recenti</h2>
              <Link to="/dashboard/messaggi" className="view-all">
                Visualizza Tutti
              </Link>
            </div>

            {messaggiBloccati && messaggi.length > 0 ? (
              <div className="messages-locked">
                <Lock size={28} />
                <p>Hai <strong>{stats.totalMessaggi}</strong> messaggi. Passa a Pro per leggerli e rispondere.</p>
                <button className="btn btn-primary" onClick={() => setPlanOpen(true)}>Sblocca con Pro</button>
              </div>
            ) : messaggi.length > 0 ? (
              <div className="messages-list">
                {messaggi.map((msg) => (
                  <div key={msg.id} className="message-item">
                    <div className="message-header">
                      <strong>{msg.mittente_nome}</strong>
                      {!msg.letto && <span className="unread-badge">Non letto</span>}
                    </div>
                    <p className="message-text">{(msg.messaggio || '').substring(0, 100)}...</p>
                    <p className="message-time">
                      {new Date(msg.created_at).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>Non hai ancora ricevuto messaggi.</p>
              </div>
            )}
          </section>
        </div>
      </div>

      <ConfirmModal
        open={!!modal}
        title={modal?.title}
        message={modal?.message}
        icon={modal?.icon}
        variant={modal?.variant}
        confirmLabel={modal?.confirmLabel}
        cancelLabel={modal?.cancelLabel}
        onConfirm={modal?.onConfirm || (() => setModal(null))}
        onClose={() => setModal(null)}
      />

      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
      <PlanModal open={planOpen} onClose={() => setPlanOpen(false)} />
    </div>
  );
}

export default ProviderDashboard;
